import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../assets/images/logodel.svg";
import "./styles/Navbar.scss";

// Split explicitly rather than by index, so the desktop bar and the mobile
// overlay can never drift out of sync (an earlier index-slice dropped
// Success Stories from the desktop bar entirely).
const LINKS_BEFORE_MENU = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
];

const MENU_LINKS = [
  { to: "/advisoryboard", label: "Advisory Board & Mentors", shortLabel: "Advisory Board" },
  { to: "/faq", label: "FAQ" },
];

const LINKS_AFTER_MENU = [
  { to: "/success-stories", label: "Success Stories" },
  { to: "/press", label: "Press" },
  { to: "/media", label: "Media" },
  { to: "/contact", label: "Contact" },
];

const ALL_LINKS = [...LINKS_BEFORE_MENU, ...LINKS_AFTER_MENU, ...MENU_LINKS];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const isAtBottom = currentScrollY + windowHeight >= documentHeight - 10;

    setScrolled(currentScrollY > 40);

    if (currentScrollY > 80 && currentScrollY > lastScrollY && !isAtBottom) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Close mobile menu on route change + lock body scroll while open
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <header className={`nav ${scrolled ? "nav--solid" : ""} ${hidden ? "nav--hidden" : ""}`}>
        <NavLink to="/" className="nav__brand" onClick={handleLogoClick}>
          <img src={logo} alt="Del Music Foundation" className="nav__logo" />
          <span className="nav__brand-text">
            Del Music <span>Foundation</span>
          </span>
        </NavLink>

        <nav className="nav__links">
          {LINKS_BEFORE_MENU.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `nav__link ${isActive ? "is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}

          <div
            className="nav__item"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <span className="nav__link nav__link--has-menu">
              More
              <span className={`nav__caret ${dropdownOpen ? "is-open" : ""}`}>▾</span>
            </span>
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  className="nav__dropdown"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                >
                  {MENU_LINKS.map((l) => (
                    <NavLink key={l.to} to={l.to} className="nav__dropdown-item">
                      {l.label}
                    </NavLink>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {LINKS_AFTER_MENU.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `nav__link ${isActive ? "is-active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}

          <NavLink to="/donations" className="nav__cta">
            Donate
          </NavLink>
        </nav>

        <button
          className={`nav__burger ${menuOpen ? "is-open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="nav__overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="nav__overlay-links">
              {ALL_LINKS.map(
                (l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i + 0.1, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.end}
                      className={({ isActive }) => `nav__overlay-link ${isActive ? "is-active" : ""}`}
                    >
                      {l.shortLabel || l.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <NavLink to="/donations" className="nav__overlay-cta">
                  Donate
                </NavLink>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
